import { createFunctionLibrary } from '@wix/thunderbolt-becky-root'
import type { BeckyRequestPayload, SiteAssetsModule } from '@wix/thunderbolt-becky-root'
import { flattenStructure } from '../flattenStructure'
import { resolveBeckyModel } from '../siteAssetsModules/helpers/resolveBeckyModel'
import { Component, DeepStructure, Structure } from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import { resolveByRefComponents } from './resolveByRefComponents'
import { getRequestedPageCompId } from '../siteAssetsModules/helpers/processedPages'
import { getBeckyTopology } from '../siteAssetsModules/helpers/topologyBuilder'
import { resolveAppWidgetsGhosts } from './appWidgetGhosts/appWidgetsGhosts'
import { deStringifyExperiments } from '../siteAssetsModules/helpers/experimentsParser'
import { BeckyFetch } from '../siteAssetsModules/utils/beckyFetch'
import { resolveSvgItemsFactory } from '../siteAssetsModules/helpers/resolveSvgItems'

export const resolveByRef = resolveByRefComponents
export const buildDeepStructure = (comp: Component, flatStructure: Structure): DeepStructure => {
	// @ts-ignore
	const children = comp.children || comp.components
	if (!_.isEmpty(children)) {
		const components = _.map(children, (child) => buildDeepStructure(flatStructure[child], flatStructure))
		// @ts-ignore
		return _(comp)
			.omit(['components', 'parent'])
			.defaults({ [comp.id === 'masterPage' ? 'children' : 'components']: components })
			.value()
	}
	// @ts-ignore
	return _.omit(_.clone(comp), 'parent')
}

const byRefModule: SiteAssetsModule<BeckyRequestPayload> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const functionLibrary = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
	})

	const resolveAndAssignSvgItems = resolveSvgItemsFactory({ functionLibrary, fetch: fetchFn, logger })

	return {
		execute: async (payload) => {
			const { beckyExperiments, pages } = payload

			const experiments = deStringifyExperiments(beckyExperiments)
			const beckyModel = await resolveBeckyModel({
				payload,
				beckyTopology: getBeckyTopology(
					topologyGetter,
					payload,
					experiments['specs.thunderbolt.relativeMediaRoot']
				),
				fetchFn,
				metricsReporter,
				shouldResolveData: false,
			})

			const masterPage = await pages.masterPage

			// AppWidgets may contain deleted RefComponents (which will be resolved next), so AppWidgets must be resolved first
			return resolveAppWidgetsGhosts(
				beckyModel,
				flattenStructure(masterPage.structure, payload.viewMode === 'mobile'),
				masterPage.data,
				functionLibrary,
				payload,
				logger
			)
				.then(
					({
						structure: withAppWidgetsGhostsStructure,
						data: withAppWidgetsGhostsData,
						masterPageStructure: withAppWidgetsGhostsMasterPageStructure,
						masterPageData: withAppWidgetsGhostsMasterPageData,
					}) => {
						return resolveByRefComponents(
							{ ...beckyModel, structure: withAppWidgetsGhostsStructure, data: withAppWidgetsGhostsData },
							withAppWidgetsGhostsMasterPageStructure,
							withAppWidgetsGhostsMasterPageData,
							functionLibrary,
							payload
						)
					}
				)
				.then(async ({ structure, data, translations }) => {
					await resolveAndAssignSvgItems(data, beckyModel.environmentTopology)
					return { structure, data, translations }
				})
				.then(async ({ structure, data, translations }) => {
					const requestedPageCompId = getRequestedPageCompId(pages)
					const currentPage = await pages[requestedPageCompId]

					pages[requestedPageCompId] = Promise.resolve({
						...currentPage,
						data,
						translations,
						structure: buildDeepStructure(structure[requestedPageCompId], structure),
					})

					return payload
				})
		},
	}
}

export default byRefModule
